/**
 * Messages API Client - User engagement messaging
 *
 * Provides API calls for user-facing message operations:
 * - Get pending messages
 * - Record impressions
 * - Dismiss messages
 */

import { api } from './client';

/**
 * Get highest-priority pending message for current user.
 * @returns {Promise<{data: object|null}>} Message object or null
 */
export async function getPending() {
  return await api.get('/messages/pending');
}

/**
 * Record that a message was shown to the user.
 * @param {string} messageId - UUID of the message
 * @returns {Promise<{data: {recorded: boolean}}>}
 */
export async function recordImpression(messageId) {
  return await api.post(`/messages/${messageId}/impression`);
}

/**
 * Record message dismissal.
 * @param {string} messageId - UUID of the message
 * @param {boolean} dontShowAgain - Whether to show this message again
 * @param {string} actionTaken - Action that triggered dismissal
 * @returns {Promise<{data: {recorded: boolean, next_message: object|null}}>}
 */
export async function dismiss(messageId, dontShowAgain, actionTaken) {
  return await api.post(`/messages/${messageId}/dismiss`, {
    dont_show_again: dontShowAgain,
    action_taken: actionTaken
  });
}

/**
 * Get urgent messages for polling.
 * Returns urgent messages that match the current user's targeting criteria.
 * @returns {Promise<{data: Array}>} Array of urgent messages
 */
export async function getUrgent() {
  return await api.get('/messages/urgent');
}

/**
 * Open one published explainer. Closing it does not keep it closed.
 * @param {string} messageId - UUID of the explainer
 * @returns {Promise<{data: object}>}
 */
export async function getOnDemand(messageId) {
  return await api.get(`/messages/${messageId}`);
}
